import { forwardRef, useCallback, useLayoutEffect, useRef, useState } from "react";
import { formatNumberInput, normalizeNumberValue, numericCaretPosition, parsePastedNumber } from "../../utils/helpers/numberInput";
import "./style.css";

const canonicalValue = (value) => value === "-" ? "" : value;

// Form handlers always receive an unformatted value, including on blur.
// Preserve DOM properties/methods used by existing validation handlers.
const numberEvent = (event, value) => {
  const target = new Proxy(event.currentTarget, {
    get(node, property) {
      if (property === "value") return value;
      if (property === "valueAsNumber") return value === "" ? NaN : Number(value);
      const result = Reflect.get(node, property, node);
      return typeof result === "function" ? result.bind(node) : result;
    },
  });
  return {
    ...event,
    target,
    currentTarget: target,
    preventDefault: () => event.preventDefault(),
    stopPropagation: () => event.stopPropagation(),
  };
};

const NumberInput = forwardRef(({
  value,
  onChange,
  onBlur,
  onFocus,
  onPaste,
  onWheel,
  onKeyDown,
  type: _type,
  inputMode,
  min,
  max,
  step,
  name,
  disabled,
  readOnly,
  ...props
}, forwardedRef) => {
  const inputRef = useRef(null);
  const pendingCaret = useRef(null);
  // Some forms store Number(value). Keep incomplete decimals/empty input locally
  // while editing, but immediately respect external changes or form clamping.
  const [edit, setEdit] = useState(null);
  const isEditing = edit !== null && Number(canonicalValue(edit.value) || 0) === Number(value || 0);
  const rawValue = isEditing ? edit.value : String(value ?? "");
  const cleanValue = canonicalValue(rawValue);
  const displayValue = formatNumberInput(rawValue, { editing: isEditing });

  const setRef = useCallback((node) => {
    inputRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }, [forwardedRef]);

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const number = Number(cleanValue);
    let message = rawValue === "-" ? "Masukkan angka yang valid." : "";
    if (cleanValue !== "") {
      if (!Number.isFinite(number)) message = "Masukkan angka yang valid.";
      else if (min !== undefined && number < Number(min)) message = `Nilai minimal ${formatNumberInput(min)}.`;
      else if (max !== undefined && number > Number(max)) message = `Nilai maksimal ${formatNumberInput(max)}.`;
      else if (Number(step) > 0) {
        const steps = (number - Number(min || 0)) / Number(step);
        if (Math.abs(steps - Math.round(steps)) > 1e-7) message = `Gunakan kelipatan ${formatNumberInput(step)}.`;
      }
    }
    input.setCustomValidity(message);
    if (pendingCaret.current !== null) {
      const position = numericCaretPosition(displayValue, pendingCaret.current);
      input.setSelectionRange(position, position);
      pendingCaret.current = null;
    }
  });

  const changeDisplay = (event, text, caret) => {
    if (disabled || readOnly) return;
    const nextValue = normalizeNumberValue(text);
    if (nextValue === null) {
      event.currentTarget.value = displayValue;
      return;
    }
    pendingCaret.current = text.slice(0, caret).replace(/[^\d,-]/g, "").length;
    setEdit({ value: nextValue });
    onChange?.(numberEvent(event, canonicalValue(nextValue)));
  };

  const insertText = (event, insertion) => {
    const input = event.currentTarget;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    changeDisplay(event, input.value.slice(0, start) + insertion + input.value.slice(end), start + insertion.length);
  };

  const handleKeyDown = (event) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || disabled || readOnly || event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key === "." || event.key === "Decimal") {
      event.preventDefault();
      insertText(event, ",");
    } else if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      const increment = Number(step) > 0 ? Number(step) : 1;
      const fraction = formatNumberInput(increment).split(",")[1]?.length || 0;
      const currentFraction = formatNumberInput(cleanValue).split(",")[1]?.length || 0;
      let next = Number((Number(cleanValue || 0) + (event.key === "ArrowUp" ? increment : -increment)).toFixed(Math.min(15, Math.max(fraction, currentFraction))));
      if (min !== undefined) next = Math.max(Number(min), next);
      if (max !== undefined) next = Math.min(Number(max), next);
      const display = formatNumberInput(next);
      changeDisplay(event, display, display.length);
    } else if (event.key === "Backspace" || event.key === "Delete") {
      const input = event.currentTarget;
      const caret = input.selectionStart;
      if (caret !== input.selectionEnd) return;
      // Skip grouping dots during deletion so a separator cannot trap the cursor.
      if (event.key === "Backspace" && input.value[caret - 1] === ".") {
        event.preventDefault();
        changeDisplay(event, input.value.slice(0, caret - 2) + input.value.slice(caret), caret - 2);
      } else if (event.key === "Delete" && input.value[caret] === ".") {
        event.preventDefault();
        changeDisplay(event, input.value.slice(0, caret) + input.value.slice(caret + 2), caret);
      }
    }
  };

  return (
    <input
      {...props}
      ref={setRef}
      name={name}
      disabled={disabled}
      readOnly={readOnly}
      type="text"
      role="spinbutton"
      inputMode={inputMode || (String(step) === "1" ? "numeric" : "decimal")}
      aria-valuenow={cleanValue !== "" && Number.isFinite(Number(cleanValue)) ? Number(cleanValue) : undefined}
      aria-valuemin={min === undefined ? undefined : Number(min)}
      aria-valuemax={max === undefined ? undefined : Number(max)}
      value={displayValue}
      data-number-input="true"
      data-min={min}
      data-max={max}
      data-step={step}
      autoComplete="off"
      onChange={(event) => {
        let text = event.currentTarget.value;
        const caret = event.currentTarget.selectionStart ?? text.length;
        // Virtual keyboards may insert a decimal dot without a keydown event.
        if (event.nativeEvent?.data === ".") text = text.slice(0, caret - 1) + "," + text.slice(caret);
        changeDisplay(event, text, caret);
      }}
      onFocus={(event) => onFocus?.(numberEvent(event, cleanValue))}
      onBlur={(event) => {
        setEdit(null);
        onBlur?.(numberEvent(event, cleanValue));
      }}
      onPaste={(event) => {
        onPaste?.(event);
        if (event.defaultPrevented || disabled || readOnly) return;
        if (!event.clipboardData) return;
        event.preventDefault();
        const parsed = parsePastedNumber(event.clipboardData.getData("text"));
        if (parsed !== null) insertText(event, formatNumberInput(parsed, { editing: true }));
      }}
      onWheel={(event) => {
        onWheel?.(event);
        if (!onWheel && document.activeElement === event.currentTarget) event.currentTarget.blur();
      }}
      onKeyDown={handleKeyDown}
    />
  );
});

NumberInput.displayName = "NumberInput";

export default NumberInput;
